/**
 * SuperAdmin Service - Centralized API clients for SuperAdmin operations
 */

import { usersAPI, sessionMetricsAPI, businessMetricsAPI } from '@/utils/api';
import { tenantsAPI } from '@/utils/tenantsAPI';
import { fetchWithAuth, safeJsonParse } from '@/utils/api';

const API_BASE = '/api/v1';

// ============================================================
// HELPERS
// ============================================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await safeJsonParse(response).catch(() => ({ message: 'Erreur serveur' }));
    throw new Error((typeof error.error === 'object' ? error.error?.message : error.error) || error.message || `HTTP ${response.status}`);
  }
  const json = await safeJsonParse(response);
  return json.data;
}

// ============================================================
// EXPORT API
// ============================================================

export const exportAPI = {
  async tenantsCSV(): Promise<Blob> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/export/tenants/csv`, {
      method: 'GET',

    });
    if (!response.ok) throw new Error("Erreur lors de l'export");
    return response.blob();
  },

  async tenantsExcel(): Promise<Blob> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/export/tenants/excel`, {
      method: 'GET',

    });
    if (!response.ok) throw new Error("Erreur lors de l'export");
    return response.blob();
  },

  async usersCSV(): Promise<Blob> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/export/users/csv`, {
      method: 'GET',

    });
    if (!response.ok) throw new Error("Erreur lors de l'export");
    return response.blob();
  },

  async usersExcel(): Promise<Blob> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/export/users/excel`, {
      method: 'GET',

    });
    if (!response.ok) throw new Error("Erreur lors de l'export");
    return response.blob();
  },

  async subscriptionsCSV(): Promise<Blob> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/export/subscriptions/csv`, {
      method: 'GET',

    });
    if (!response.ok) throw new Error("Erreur lors de l'export");
    return response.blob();
  },

  async companiesCSV(): Promise<Blob> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/export/companies/csv`, {
      method: 'GET',

    });
    if (!response.ok) throw new Error("Erreur lors de l'export");
    return response.blob();
  },
};

// ============================================================
// IMPORT API
// ============================================================

export interface ValidationError {
  row: number;
  field?: string;
  value?: string;
  error: string;
}

export interface ValidationResult {
  valid: boolean;
  rowCount: number;
  errors: ValidationError[];
  preview: any[];
}

export interface ImportResult {
  successCount: number;
  failureCount: number;
  errors: ValidationError[];
  importedIds: string[];
}

export const importAPI = {
  async validateUsersCSV(file: File): Promise<ValidationResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${API_BASE}/superadmin/import/users/validate`, {
      method: 'POST',

      body: formData,
    });

    return handleResponse<ValidationResult>(response);
  },

  async validatePlansCSV(file: File): Promise<ValidationResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${API_BASE}/superadmin/import/plans/validate`, {
      method: 'POST',

      body: formData,
    });

    return handleResponse<ValidationResult>(response);
  },

  async importUsers(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${API_BASE}/superadmin/import/users`, {
      method: 'POST',

      body: formData,
    });

    return handleResponse<ImportResult>(response);
  },

  async importPlans(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${API_BASE}/superadmin/import/plans`, {
      method: 'POST',

      body: formData,
    });

    return handleResponse<ImportResult>(response);
  },
};

// ============================================================
// IMPERSONATION API
// ============================================================

export interface ImpersonationSession {
  isActive: boolean;
  targetUser: {
    id: string;
    email: string;
    fullName: string;
  };
  superadminId: string;
  startedAt: string;
  accessToken?: string;
  expiresIn?: number;
}

export const impersonationAPI = {
  async checkActive(): Promise<ImpersonationSession | null> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/impersonation/active`, {
      method: 'GET',

    });

    if (!response.ok) return null;
    const data = await safeJsonParse(response);
    return data.success && data.data.isActive ? data.data : null;
  },

  async start(userId: string): Promise<ImpersonationSession> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/impersonate/${userId}`, {
      method: 'POST',

    });

    return handleResponse<ImpersonationSession>(response);
  },

  async stop(): Promise<{ accessToken: string; expiresIn?: number }> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/stop-impersonation`, {
      method: 'POST',

    });

    return handleResponse<{ accessToken: string; expiresIn?: number }>(response);
  },

  async getHistory(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    superadminId?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.superadminId) queryParams.append('superadminId', params.superadminId);

    const response = await fetchWithAuth(
      `${API_BASE}/superadmin/impersonation/history?${queryParams.toString()}`,
      {
        method: 'GET',
  
      }
    );

    return handleResponse(response);
  },
};

// ============================================================
// BULK ACTIONS API
// ============================================================

export interface BulkActionResult {
  successCount: number;
  failureCount: number;
  errors: Array<{ id: string; error: string }>;
  processedIds: string[];
}

export const bulkActionsAPI = {
  async blockUsers(userIds: string[], reason?: string): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/users/block`, {
      method: 'POST',

      body: JSON.stringify({ userIds, reason: reason || 'Blocked by superadmin' }),
    });

    return handleResponse<BulkActionResult>(response);
  },

  async unblockUsers(userIds: string[]): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/users/unblock`, {
      method: 'POST',

      body: JSON.stringify({ userIds }),
    });

    return handleResponse<BulkActionResult>(response);
  },

  async suspendTenants(tenantIds: string[], reason: string): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/tenants/suspend`, {
      method: 'POST',

      body: JSON.stringify({ tenantIds, reason }),
    });

    return handleResponse<BulkActionResult>(response);
  },

  async activateTenants(tenantIds: string[]): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/tenants/activate`, {
      method: 'POST',

      body: JSON.stringify({ tenantIds }),
    });

    return handleResponse<BulkActionResult>(response);
  },

  async changeTenantsPlan(tenantIds: string[], planId: string): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/tenants/change-plan`, {
      method: 'POST',

      body: JSON.stringify({ tenantIds, planId }),
    });

    return handleResponse<BulkActionResult>(response);
  },

  async deleteUsers(userIds: string[]): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/users/delete`, {
      method: 'DELETE',

      body: JSON.stringify({ userIds, confirmation: 'DELETE_USERS_CONFIRMED' }),
    });

    return handleResponse<BulkActionResult>(response);
  },

  async deleteTenants(tenantIds: string[]): Promise<BulkActionResult> {
    const response = await fetchWithAuth(`${API_BASE}/superadmin/bulk/tenants/delete`, {
      method: 'DELETE',

      body: JSON.stringify({ tenantIds, confirmation: 'DELETE_TENANTS_CONFIRMED' }),
    });

    return handleResponse<BulkActionResult>(response);
  },
};

// ============================================================
// MAIN SERVICE EXPORT
// ============================================================

export const superadminService = {
  users: usersAPI,
  tenants: tenantsAPI,
  sessionMetrics: sessionMetricsAPI,
  businessMetrics: businessMetricsAPI,
  export: exportAPI,
  import: importAPI,
  impersonation: impersonationAPI,
  bulkActions: bulkActionsAPI,
};
